import { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { naturesNestProposal } from "../data/proposals/natures-nest";
import { planetLifeProposal } from "../data/proposals/planet-life";
import useEngagementTracker from "./useEngagementTracker";
import {
  ThrushBird,
  FlycatcherBird,
  HornbillBird,
  HeroFlock
} from "./Birds";
import "./proposal.css";

// Map tier id → bird component (rendered inside the CTA button)
const tierBirdComponents = {
  thrush: ThrushBird,
  flycatcher: FlycatcherBird,
  hornbill: HornbillBird
};

// Playful CTA copy — the buttons don't go anywhere; they just let the bird fly.
// Verb-escalation: companion → partner → ambition.
const birdCopy = {
  thrush: "Take me with you",
  flycatcher: "Fly with me",
  hornbill: "Let's go far"
};

const proposalRegistry = {
  "natures-nest": naturesNestProposal,
  "planet-life": planetLifeProposal
};

const proofPool = {
  primary: {
    tag: "Hospitality / brand-led conversion",
    title: "A boutique stay, rebuilt around the booking decision.",
    body:
      "We rebuilt a small premium-stay's site around the decision moment — when a casual reader becomes a guest. Stripped four pages, rewrote three, integrated their booking engine inline.",
    metric: "+38% direct bookings, in 90 days post-launch."
  },
  side: [
    {
      strong: "An editorial-led commerce site",
      span: "Where atmosphere had to do the selling. Performance-budget kept under 1.4s on 4G."
    },
    {
      strong: "A multi-property travel brand",
      span: "Headless rebuild, single CMS, four sub-brands sharing one design system."
    },
    {
      strong: "A wellness retreat in the Western Ghats",
      span: "Story-led rewrite that lifted average session depth by 2.4x."
    }
  ]
};

function PriceCounter({ target, active, delay = 0, className }) {
  const [val, setVal] = useState(0);
  useEffect(() => {
    if (!active) return;
    let raf;
    let started = false;
    let startTs = 0;
    const duration = 1300;
    const start = () => {
      started = true;
      startTs = performance.now();
      const step = (ts) => {
        const t = Math.min(1, (ts - startTs) / duration);
        const eased = 1 - Math.pow(1 - t, 3);
        setVal(Math.round(target * eased));
        if (t < 1) raf = requestAnimationFrame(step);
      };
      raf = requestAnimationFrame(step);
    };
    const timer = setTimeout(start, delay);
    return () => {
      clearTimeout(timer);
      if (raf) cancelAnimationFrame(raf);
      if (!started) setVal(0);
    };
  }, [active, target, delay]);
  return <span className={className}>{formatINR(val)}</span>;
}

function formatINR(n) {
  if (!n) return "₹0";
  const s = Math.round(n).toString();
  if (s.length <= 3) return "₹" + s;
  const last3 = s.slice(-3);
  const rest = s.slice(0, -3);
  const withCommas = rest.replace(/\B(?=(\d{2})+(?!\d))/g, ",");
  return "₹" + withCommas + "," + last3;
}

// Per-word reveal — spaces are real text nodes BETWEEN spans, not inside them.
function WordReveal({ text, baseDelay = 0 }) {
  const words = text.split(/\s+/);
  return (
    <>
      {words.map((w, i) => (
        <span key={i}>
          <span
            className="pp-word"
            style={{ animationDelay: `${baseDelay + i * 22}ms` }}
          >
            {w}
          </span>
          {i < words.length - 1 ? " " : ""}
        </span>
      ))}
    </>
  );
}

export default function ProposalPage() {
  const { slug } = useParams();
  const config = proposalRegistry[slug];

  const tracker = useEngagementTracker(slug);
  const [tiersVisible, setTiersVisible] = useState(false);
  const [observedVisible, setObservedVisible] = useState(false);
  // null = no choice yet, else the chosen tier id
  const [chosenBird, setChosenBird] = useState(null);

  const progressRef = useRef(null);
  const tiersRef = useRef(null);
  const observedRef = useRef(null);
  const heroRef = useRef(null);
  const spotRef = useRef(null);

  useEffect(() => {
    if (!config) return;
    let raf = 0;
    const updateProgress = () => {
      raf = 0;
      const h = document.documentElement;
      const total = h.scrollHeight - window.innerHeight;
      if (total <= 0) return;
      const pct = Math.min(100, (window.scrollY / total) * 100);
      if (progressRef.current) {
        progressRef.current.style.transform = `scaleX(${pct / 100})`;
      }
    };
    const onScroll = () => {
      if (!raf) raf = requestAnimationFrame(updateProgress);
    };
    updateProgress();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      window.removeEventListener("scroll", onScroll);
      if (raf) cancelAnimationFrame(raf);
    };
  }, [config]);

  useEffect(() => {
    if (!config) return;
    const observers = [];
    if (tiersRef.current) {
      const o = new IntersectionObserver(
        (entries) => entries.forEach((e) => { if (e.isIntersecting) setTiersVisible(true); }),
        { threshold: 0.2 }
      );
      o.observe(tiersRef.current);
      observers.push(o);
    }
    if (observedRef.current) {
      const o = new IntersectionObserver(
        (entries) => entries.forEach((e) => { if (e.isIntersecting) setObservedVisible(true); }),
        { threshold: 0.4 }
      );
      o.observe(observedRef.current);
      observers.push(o);
    }
    return () => observers.forEach((o) => o.disconnect());
  }, [config]);

  // Cursor-tracking warm spotlight (hero only) — rAF-throttled
  useEffect(() => {
    if (!config) return;
    let lastX = 0;
    let lastY = 0;
    let pending = false;
    const apply = () => {
      pending = false;
      if (!spotRef.current || !heroRef.current) return;
      const rect = heroRef.current.getBoundingClientRect();
      const inHero = lastY >= rect.top - 100 && lastY <= rect.bottom + 100;
      if (inHero) {
        spotRef.current.classList.add("is-on");
        spotRef.current.style.transform = `translate3d(${lastX}px, ${lastY}px, 0) translate(-50%, -50%)`;
      } else {
        spotRef.current.classList.remove("is-on");
      }
    };
    const onMove = (e) => {
      lastX = e.clientX;
      lastY = e.clientY;
      if (!pending) {
        pending = true;
        requestAnimationFrame(apply);
      }
    };
    window.addEventListener("mousemove", onMove, { passive: true });
    return () => window.removeEventListener("mousemove", onMove);
  }, [config]);

  useEffect(() => {
    if (config) document.title = `${config.client.name} · Proposal · Sutranode`;
    return () => { document.title = "Sutranode"; };
  }, [config]);

  if (!config) {
    return (
      <div className="pp-invalid">
        <div className="pp-invalid-inner">
          <h1>This proposal isn't available.</h1>
          <p>Either the link's wrong, or the proposal has been retired. If you think this is a mistake, reply to the email it came in.</p>
        </div>
      </div>
    );
  }

  const handlePdfExport = () => {
    tracker.recordPdfExport();
    tracker.recordCta("pdf-export");
    window.print();
  };

  const handleIntent = (tierId) => {
    tracker.recordIntent(tierId);
    tracker.recordCta("intent", { tier: tierId });
    if (!config.next?.disableNavigation && config.next?.primary?.href) {
      window.open(config.next.primary.href, "_blank", "noopener,noreferrer");
    }
  };

  const tiers = config.tiers;

  return (
    <>
      <div className="pp-atmosphere" aria-hidden="true">
        <div className="pp-atm-mesh" />
        <div className="pp-atm-orb pp-atm-orb-1" />
        <div className="pp-atm-orb pp-atm-orb-2" />
        <div className="pp-atm-orb pp-atm-orb-3" />
        <div className="pp-atm-grain" />
        <div ref={spotRef} className="pp-atm-spot" />
      </div>

      <div className="pp-progress">
        <div ref={progressRef} className="pp-progress-fill" />
      </div>

      <div className="pp-page">
        <div className="pp-shell">
          <div className="pp-letterhead">
            <div className="pp-letterhead-left">
              <div className="pp-letterhead-mark" aria-hidden="true" />
              <div className="pp-letterhead-text">
                <span className="pp-letterhead-title">Sutranode · Studio Proposal</span>
                <span className="pp-letterhead-sub">{config.meta.docCode}</span>
              </div>
            </div>
            <div className="pp-letterhead-right">
              <span>Prepared for <strong>{config.client.name}</strong></span>
              <span>{config.meta.preparedDate}</span>
              <button
                className="pp-pdf-btn"
                onClick={handlePdfExport}
                aria-label="Save proposal as PDF"
              >
                Save as PDF
              </button>
            </div>
          </div>

          <section className="pp-hero" ref={(el) => { heroRef.current = el; tracker.observeSection("hero")(el); }}>
            <HeroFlock />
            <div className="pp-hero-meta">
              <span>Prepared by <strong>{config.meta.preparedBy}</strong></span>
              <span>For <strong>{config.meta.preparedFor}</strong></span>
              <span>Sector <strong>{config.client.sector}</strong></span>
            </div>
            <h1 className="pp-hero-headline">{config.meta.projectTitle}</h1>
            <div className="pp-hero-framing">{config.framing}</div>
          </section>

          <section
            className={`pp-section pp-observed ${observedVisible ? "is-visible" : ""}`}
            ref={(el) => { observedRef.current = el; tracker.observeSection("observed")(el); }}
          >
            <div className="pp-eyebrow">{config.observed.eyebrow}</div>
            <div className="pp-observed-prose">
              {config.observed.paragraphs.map((p, i) => (
                <p key={i}>
                  <WordReveal text={p} baseDelay={i * 600} />
                </p>
              ))}
            </div>
          </section>

          <section className="pp-section" ref={tracker.observeSection("belief")}>
            <div className="pp-eyebrow">{config.belief.eyebrow}</div>
            <h2 className="pp-belief-headline">{renderWithEm(config.belief.headline)}</h2>
            <p className="pp-prose">{config.belief.body}</p>
          </section>

          <section className="pp-section" ref={tracker.observeSection("paths")}>
            <div className="pp-paths-intro">
              <div className="pp-eyebrow">{config.pathsIntro.eyebrow}</div>
              <h2 className="pp-h2">{config.pathsIntro.headline}</h2>
              <p className="pp-prose">{config.pathsIntro.body}</p>
            </div>

            <div className={`pp-tiers ${tiersVisible ? "is-visible" : ""}`} ref={tiersRef}>
              {tiers.map((t, idx) => {
                const TierBird = tierBirdComponents[t.id];
                return (
                <article
                  key={t.id}
                  className={`pp-tier pp-tier-${t.id}`}
                  onMouseEnter={() => tracker.onTierEnter(t.id)}
                  onMouseLeave={() => tracker.onTierLeave(t.id)}
                >
                  <div className="pp-tier-sheen" aria-hidden="true" />
                  {t.mostChosen && <span className="pp-tier-badge">Most chosen</span>}
                  {t.flagship && <span className="pp-tier-badge pp-tier-badge-flagship">Flagship</span>}

                  <div className="pp-tier-bird">
                    <span className="pp-tier-bird-label">{t.bird}</span>
                    <span className="pp-tier-bird-name">{t.bird}</span>
                    <span className="pp-tier-bird-sub">{t.birdSub}</span>
                    <p className="pp-tier-bird-note">{t.birdNote}</p>
                  </div>

                  <div className="pp-tier-tagline">{t.tagline}</div>

                  <div className="pp-tier-price">
                    <PriceCounter
                      target={t.price}
                      active={tiersVisible}
                      delay={idx === 2 ? 320 : idx === 1 ? 160 : 0}
                      className="pp-tier-price-num"
                    />
                    <span className="pp-tier-price-justify">{t.priceJustification}</span>
                    <span className="pp-tier-timeline">Timeline · {t.timeline}</span>
                  </div>

                  <p className="pp-tier-best">{t.bestFor}</p>

                  <div>
                    <div className="pp-tier-list-label">What's included</div>
                    <ul className="pp-tier-list">
                      {t.includes.map((line, i) => (
                        <li key={i} className={line.startsWith("Plus:") ? "is-gift" : ""}>{line}</li>
                      ))}
                    </ul>
                    {t.excludes && t.excludes.length > 0 && (
                      <>
                        <div className="pp-tier-excludes-label">Doesn't include</div>
                        <ul className="pp-tier-excludes">
                          {t.excludes.map((line, i) => <li key={i}>{line}</li>)}
                        </ul>
                      </>
                    )}
                  </div>

                  <p className="pp-tier-outcome">In 12 months: {t.twelveMonthOutcome}</p>

                  <button
                    className={`pp-tier-cta pp-cta-with-bird pp-cta-${t.id} ${
                      chosenBird === null
                        ? ""
                        : chosenBird === t.id
                        ? "is-chosen"
                        : "is-flown"
                    }`}
                    onClick={() => {
                      if (chosenBird !== null) return; // single-shot
                      setChosenBird(t.id);
                      tracker.recordCta("bird-pick", { tier: t.id });
                    }}
                    disabled={chosenBird !== null && chosenBird !== t.id}
                    aria-label={`${birdCopy[t.id]} — ${t.bird}`}
                  >
                    <span className="pp-cta-label">{birdCopy[t.id]}</span>
                    {TierBird && (
                      <span className="pp-cta-bird" aria-hidden="true">
                        <TierBird />
                      </span>
                    )}
                  </button>
                </article>
                );
              })}
            </div>
          </section>

          {!config.recommendation?.hidden && (
            <section className="pp-section" ref={tracker.observeSection("recommendation")}>
              <div className="pp-rec">
                <div className="pp-eyebrow">{config.recommendation.eyebrow}</div>
                <h2 className="pp-rec-headline">{renderWithEm(config.recommendation.headline)}</h2>
                <div className="pp-rec-body">
                  {config.recommendation.body.map((p, i) => <p key={i}>{p}</p>)}
                </div>
              </div>
            </section>
          )}

          {!config.process?.hidden && (
            <section className="pp-section" ref={tracker.observeSection("process")}>
              <div className="pp-eyebrow">{config.process.eyebrow}</div>
              <h2 className="pp-h2">{config.process.headline}</h2>
              <div className="pp-phases">
                {config.process.phases.map((p) => (
                  <div className="pp-phase" key={p.n}>
                    <div className="pp-phase-num">{p.n}</div>
                    <div>
                      <h3 className="pp-phase-title">{p.title}</h3>
                      <div className="pp-phase-weeks">{p.weeks}</div>
                      {p.line && <p className="pp-phase-line">{p.line}</p>}
                    </div>
                  </div>
                ))}
              </div>
            </section>
          )}

          <section className="pp-section" ref={tracker.observeSection("tech")}>
            <div className="pp-eyebrow">{config.tech.eyebrow}</div>
            <h2 className="pp-h2">{config.tech.headline}</h2>
            <div className="pp-tech-body">
              {config.tech.body.map((p, i) => <p key={i}>{p}</p>)}
            </div>
          </section>

          {!config.proof?.hidden && (
            <section className="pp-section" ref={tracker.observeSection("proof")}>
              <div className="pp-eyebrow">{config.proof.eyebrow}</div>
              <h2 className="pp-h2">Picked by problem-shape, not industry tag.</h2>
              <p className="pp-prose">{config.proof.note}</p>

              <div className="pp-proof-grid">
                <div className="pp-proof-card">
                  <span className="pp-proof-card-tag">{proofPool.primary.tag}</span>
                  <h4>{proofPool.primary.title}</h4>
                  <p>{proofPool.primary.body}</p>
                  <div className="pp-proof-metric">{proofPool.primary.metric}</div>
                </div>
                <div className="pp-proof-side">
                  {proofPool.side.map((s, i) => (
                    <div className="pp-proof-side-card" key={i}>
                      <strong>{s.strong}</strong>
                      <span>{s.span}</span>
                    </div>
                  ))}
                </div>
              </div>
            </section>
          )}

          <section className="pp-section" ref={tracker.observeSection("next")}>
            <div className="pp-next-card">
              <div className="pp-eyebrow">{config.next.eyebrow}</div>
              <h2 className="pp-h2">{config.next.headline}</h2>
              <p className="pp-prose">{config.next.note || "There's no pressure to commit on this page. Reply, call, or pick a tier — whichever feels right."}</p>
              <div className="pp-next-actions">
                {config.next.disableNavigation ? (
                  <button
                    className="pp-next-primary"
                    type="button"
                    onClick={() => tracker.recordCta("call")}
                  >
                    {config.next.primary.label} →
                  </button>
                ) : (
                  <a
                    className="pp-next-primary"
                    href={config.next.primary.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={() => tracker.recordCta("call")}
                  >
                    {config.next.primary.label} →
                  </a>
                )}
                <button
                  className="pp-next-secondary"
                  onClick={() => handleIntent("flycatcher")}
                >
                  {config.next.secondary.label}
                </button>
                <span className="pp-next-tertiary">{config.next.tertiary.label}</span>
              </div>
            </div>
          </section>

          <section className="pp-section pp-sources" ref={tracker.observeSection("sources")}>
            <div className="pp-eyebrow">{config.notesAndSources.eyebrow}</div>
            <p className="pp-prose">{config.notesAndSources.note}</p>
            <ul className="pp-sources-list">
              {config.notesAndSources.items.map((it, i) => <li key={i}>{it}</li>)}
            </ul>
          </section>

          <div className="pp-validity">
            <p>{config.validity.line}</p>
            <div className="pp-validity-sig">{config.validity.signature}</div>
          </div>
        </div>
      </div>
    </>
  );
}

function renderWithEm(text) {
  if (!text) return null;
  const parts = text.split(/(\*[^*]+\*)/g);
  return parts.map((p, i) => {
    if (p.startsWith("*") && p.endsWith("*")) return <em key={i}>{p.slice(1, -1)}</em>;
    return <span key={i}>{p}</span>;
  });
}
